import { useMemo, useState } from "react";
import Input from "../components/common/Input";
import Button from "../components/common/Button";
import platformHeroImage from "../assets/child-hero.webp";
import { submitChildReferral } from "../lib/api";

const initialForm = {
  referrerName: "",
  referrerPhone: "",
  referredName: "",
  referredPhone: "",
  managerName: "",
  privacyConsent: false,
};

const ErrorText = ({ msg }) => (
  <div className="h-[20px]">
    <p
      className={`text-sm text-red-600 transition-opacity ${
        msg ? "opacity-100" : "opacity-0"
      }`}
    >
      {msg || " "}
    </p>
  </div>
);

const onlyDigits = (value) => value.replace(/\D/g, "");

const isValidPhone = (value) => {
  const digits = onlyDigits(value);
  return digits.length >= 10 && digits.length <= 11;
};

export default function ChildReferralEventPage() {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const errors = useMemo(() => {
    const e = {};

    if (!form.referrerName.trim()) {
      e.referrerName = "* 우수고객 성함을 입력하세요.";
    }

    if (!isValidPhone(form.referrerPhone)) {
      e.referrerPhone = "* 우수고객 휴대전화를 입력하세요.";
    }

    if (!form.referredName.trim()) {
      e.referredName = "* 소개하실 분 성함을 입력하세요.";
    }

    if (!isValidPhone(form.referredPhone)) {
      e.referredPhone = "* 소개하실 분 휴대전화를 입력하세요.";
    }

    if (!form.privacyConsent) {
      e.privacyConsent = "* 개인정보 수집 및 이용에 동의해 주세요.";
    }

    return e;
  }, [form]);

  const canSubmit = Object.keys(errors).length === 0;

  const handleChange = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || loading) return;

    const confirmed = window.confirm(
      "어린이보험 지인 소개 이벤트 신청을 진행하시겠습니까?",
    );

    if (!confirmed) return;

    setLoading(true);
    setDone(false);

    try {
      const payload = {
        name: form.referrerName.trim(),
        phone: onlyDigits(form.referrerPhone),
        refereeName: form.referredName.trim(),
        refereePhone: onlyDigits(form.referredPhone),
        manager: form.managerName.trim(),
      };

      await submitChildReferral(payload);

      alert("신청이 완료되었습니다.");
      setDone(true);
      setForm(initialForm);
    } catch (error) {
      console.error(error);
      alert("전송에 실패했습니다. 잠시 후 다시 시도해주세요.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="bg-white">
      <section className="mx-auto w-full max-w-5xl px-5 py-10 md:px-6 md:py-14">
        <div className="overflow-hidden rounded-[28px] border border-sky-100 bg-white shadow-[0_10px_30px_rgba(0,0,0,0.06)]">
          <div className="relative overflow-hidden">
            <img
              src={platformHeroImage}
              alt="어린이보험 이벤트 배경"
              className="h-[380px] w-full object-cover object-center"
            />

            <div className="absolute inset-0 bg-gradient-to-r from-black/45 via-black/20 to-transparent" />

            <div className="absolute inset-0 flex flex-col justify-center px-6 py-8 text-white md:px-8 md:py-10">
              <p className="mb-3 text-sm font-bold tracking-[0.08em] drop-shadow-md">
                CHILD INSURANCE REFERRAL EVENT
              </p>

              <h1 className="text-[28px] font-extrabold leading-tight drop-shadow-md md:text-[34px]">
                어린이보험 상담 소개 이벤트
              </h1>

              <p className="mt-4 leading-6 drop-shadow-md md:text-base">
                소중한 지인을 소개해 주시면 감사의 마음을 담아 선물을 드립니다.
              </p>
            </div>
          </div>

          <div className="px-6 py-7 md:px-8 md:py-8">
            <div className="rounded-2xl border border-sky-200 bg-sky-50 p-5">
              <p className="text-lg font-extrabold text-sky-500">
                🎁 지인 소개 이벤트 안내
              </p>

              <div className="mt-4 space-y-3 leading-6 text-gray-800">
                <p>
                  소개해주신 지인이 가입하시면
                  <span className="ml-2 font-extrabold text-sky-500">
                    더 많은 신세계 상품권 증정
                  </span>
                </p>

                <p className="pt-1 text-sm text-gray-600">
                  ※ 소중한 인연을 소개해주셔서 진심으로 감사드립니다.
                </p>
              </div>

              <div className="mt-5 grid gap-2 rounded-xl bg-white p-4 font-medium text-gray-700 md:grid-cols-2">
                <div>✔ 자세한 1:1 맞춤 안내 및 상담</div>
                <div>✔ 가입 후에도 꾸준한 관리 서비스 제공</div>
              </div>

              <p className="mt-4 text-right text-xs text-gray-500">
                굿리치(주) 리치앤플러스 사업부 제공
              </p>
            </div>

            <form className="mt-8 space-y-4" onSubmit={handleSubmit}>
              <div className="space-y-1">
                <label className="font-semibold text-gray-800">
                  1. 우수고객 성함 (소개자){" "}
                  <span className="text-red-600">*</span>
                </label>
                <Input
                  placeholder="우수고객 성함을 입력해주세요"
                  value={form.referrerName}
                  onChange={(e) => handleChange("referrerName", e.target.value)}
                />
                <ErrorText msg={errors.referrerName} />
              </div>

              <div className="space-y-1">
                <label className="font-semibold text-gray-800">
                  2. 우수고객 휴대전화 <span className="text-red-600">*</span>
                </label>
                <Input
                  placeholder="숫자만 입력해주세요"
                  value={form.referrerPhone}
                  onChange={(e) =>
                    handleChange(
                      "referrerPhone",
                      onlyDigits(e.target.value).slice(0, 11),
                    )
                  }
                />
                <ErrorText msg={errors.referrerPhone} />
              </div>

              <div className="space-y-1">
                <label className="font-semibold text-gray-800">
                  3. 소개하실 분 성함 <span className="text-red-600">*</span>
                </label>
                <Input
                  placeholder="소개하실 분 성함을 입력해주세요"
                  value={form.referredName}
                  onChange={(e) => handleChange("referredName", e.target.value)}
                />
                <ErrorText msg={errors.referredName} />
              </div>

              <div className="space-y-1">
                <label className="font-semibold text-gray-800">
                  4. 소개하실 분 휴대전화{" "}
                  <span className="text-red-600">*</span>
                </label>
                <Input
                  placeholder="숫자만 입력해주세요"
                  value={form.referredPhone}
                  onChange={(e) =>
                    handleChange(
                      "referredPhone",
                      onlyDigits(e.target.value).slice(0, 11),
                    )
                  }
                />
                <ErrorText msg={errors.referredPhone} />
              </div>

              <div className="space-y-1">
                <label className="font-semibold text-gray-800">
                  5. 어린이보험 담당 매니저 (성함을 적어 주세요)
                </label>
                <Input
                  placeholder="담당 매니저 성함 입력"
                  value={form.managerName}
                  onChange={(e) => handleChange("managerName", e.target.value)}
                />
                <ErrorText msg="" />
              </div>

              <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                <div className="flex items-start gap-3">
                  <input
                    id="privacyConsent"
                    type="checkbox"
                    className="mt-1 h-4 w-4"
                    checked={form.privacyConsent}
                    onChange={(e) =>
                      handleChange("privacyConsent", e.target.checked)
                    }
                  />

                  <label
                    htmlFor="privacyConsent"
                    className="flex-1 space-y-3 leading-6 text-gray-700"
                  >
                    <span className="block font-semibold text-gray-900">
                      6. 개인정보 수집 및 이용 동의
                      <span className="ml-1 text-red-600">*</span>
                    </span>

                    <p className="text-sm text-gray-600">
                      동의를 거부하실 수 있으나 이벤트 참여가 불가능합니다.
                    </p>

                    <ul className="list-disc space-y-1 pl-5 text-sm text-gray-700">
                      <li>수집하는 개인정보 항목: 이름, 연락처</li>
                      <li>수집 및 이용 목적: 이벤트 진행 및 선물 안내</li>
                      <li>보관 및 이용 기간: 상담 진행 후 12개월 보관</li>
                    </ul>
                  </label>
                </div>

                <div className="mt-3">
                  <ErrorText msg={errors.privacyConsent} />
                </div>
              </div>

              <Button
                type="submit"
                variant="secondary"
                className="mt-2 w-full"
                disabled={!canSubmit || loading}
              >
                {loading ? "전송 중..." : "이벤트 신청하기"}
              </Button>

              {done ? (
                <p className="text-center text-sm font-semibold text-secondary">
                  신청이 완료되었습니다. 확인 후 안내드리겠습니다.
                </p>
              ) : null}
            </form>
          </div>
        </div>
      </section>
    </main>
  );
}
